import { z } from 'zod';

const nullishString = ({ max }: { max?: number } = {}) =>
  z.preprocess(
    (val) => (typeof val === 'string' ? val.trim() : val),
    (typeof max === 'number' ? z.string().max(max) : z.string())
      .nullish()
      .transform((val) => val || null),
  );

const optionalString = ({ max }: { max: number }) =>
  z.preprocess(
    (val) => (typeof val === 'string' ? val.trim() : val),
    (typeof max === 'number' ? z.string().max(max) : z.string()).optional(),
  );

const NullishString = nullishString();

const OptionalBoolean = z.preprocess(
  (val) => (typeof val === 'string' ? val.trim() : val),
  z
    .union([
      z.literal('Y'),
      z.literal('y'),
      z.literal('N'),
      z.literal('n'),
      z.literal(''),
    ])
    .nullable()
    .optional()
    .transform((value) =>
      value === 'Y' || value === 'y'
        ? true
        : value === 'N' || value === 'n'
          ? false
          : undefined,
    ),
);

const BulkDonorScheduleRowSchema = z.object({
  accountCode: z.string().min(1),
  billAccountCode: NullishString,
  donorBreed: z.string().min(1),
  donorNameToMatch: z.string().min(1).max(41),
  donorRegistrationNumber: nullishString({ max: 20 }),
  pedigreeName: nullishString({ max: 50 }),
  pregnant: OptionalBoolean,
  birthDate: NullishString,
  programName: nullishString({ max: 100 }),
  tpi: z.number().nullish(),
  nm: z.number().nullish(),
  opuProgram: NullishString,
  tattoo: nullishString({ max: 11 }),
  received: NullishString,
  yardCode: NullishString,
  sireNameToMatch: NullishString,
  sireBreed: NullishString,
  sireRegistrationNumber: NullishString,
  collectionCode: NullishString,
  semenType: NullishString,
  donorLocation: nullishString({ max: 100 }),
  scheduleCode: NullishString,
  pool: OptionalBoolean,
  aspirationComment: nullishString({ max: 100 }),
  naab: nullishString({ max: 10 }),
  cane: nullishString({ max: 30 }),
  ship: NullishString,
  shipmentGroup: NullishString,
  freeze: NullishString,
  freezeMethod: NullishString,
  export: NullishString,
  trypsinTreat: OptionalBoolean,
  nonEuCountry: NullishString,
});

const BulkDonorRowSchema = z.object({
  accountCode: z.string().min(5),
  yardCode: NullishString,
  donorBreed: z.string().min(1),
  donorNameToMatch: z.string().min(1).max(41),
  donorRegistrationNumber: optionalString({ max: 20 }),
  pedigreeName: optionalString({ max: 50 }),
  pregnant: OptionalBoolean,
  birthDate: NullishString,
  programName: optionalString({ max: 1000 }),
  tpi: z.number().nullish(),
  nm: z.number().nullish(),
  opuProgram: NullishString,
  tattoo: nullishString({ max: 11 }),
  received: NullishString,
  exportDonor: OptionalBoolean,
});

export type BulkDonorScheduleInput = z.infer<typeof BulkDonorScheduleRowSchema>;
export type BulkDonorInput = z.infer<typeof BulkDonorRowSchema>;

export const BulkDonorScheduleRowsSchema = z.array(
  BulkDonorScheduleRowSchema.optional(),
);

export const BulkDonorRowsSchema = z.array(BulkDonorRowSchema.optional());

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

export const BulkDonorScheduleFieldsSchema = z.object({
  aspirationDate: z.date().min(startOfToday()),
  fertilizationSiteId: z.string().min(1),
  aspirationSiteId: z.string().min(1),
});

export const BulkDonorFieldsSchema = z.object({
  aspirationDate: z.date().min(startOfToday()).optional(),
  fertilizationSiteId: z.string().min(1).optional(),
  aspirationSiteId: z.string().min(1).optional(),
});

export type BulkDonorScheduleFieldsInput = z.infer<
  typeof BulkDonorScheduleFieldsSchema
>;

export type BulkDonorFieldsInput = z.infer<typeof BulkDonorFieldsSchema>;
